import { useMemo } from "react";
import { omitBy, isNil } from "lodash";
import { NavigationInterface } from "Types/common.types";

interface UseNavigationProps {
  rawNavigation: NavigationInterface[];
  roles: string[];
}

const useNavigation = ({ rawNavigation, roles }: UseNavigationProps) => {
  const navigation = useMemo(() => {
    const isActive = (
      // before logged in roleTypes is undefined
      // default empty array to avoid any errors
      roleTypes: string[] = [],
      comparator: string[],
      options?: { exclude?: string[]; includeOnly?: string },
    ): boolean => {
      const checkRoleMatch = (roleType: string, comp: string): boolean => {
        const splitRoleType = roleType.split("-");
        const orgType = splitRoleType[0] || "";
        const subRole = splitRoleType[1] || "";

        if (options?.includeOnly) {
          const isIncluded =
            subRole === options.includeOnly || options.includeOnly === orgType;
          if (!isIncluded) return false;
        }

        if (options?.exclude) {
          const isExcluded =
            options.exclude.includes(subRole) ||
            options.exclude.includes(orgType) ||
            options.exclude.includes(roleType)
          if (isExcluded) return false;
        }

        const compPattern = new RegExp(comp, "g");
        return Boolean(roleType.match(compPattern)?.length);
      };

      // if (!comparator.includes("ALL"))
        return comparator.some((comp) =>
          roleTypes.some((roleType) => checkRoleMatch(roleType, comp)),
        );

      // return true;
    };

    // we process each navigation item to mainly check for the isActive value
    // this controls the visibility of the module
    // each children will be processed recursively and has the same type as its parent
    const processNavigationItem = (
      item: NavigationInterface,
      parentRoles?: string[],
    ) => {
      const navigationItem = {
        label: item.label,
        href: item.href,
        icon: item.icon,
        isActive: isActive(
          roles,
          parentRoles || item.roles || [],
          omitBy(
            {
              includeOnly: item.includeOnly,
              exclude: item.exclude,
            },
            isNil,
          ),
        ),
        children: item.children && processChildren(item.children, item.roles),
      };

      return omitBy(navigationItem, isNil);
    };

    // children can also have its own children
    // | parent
    //   |-> children
    //     |-> children
    const processChildren = (
      children: NavigationInterface[],
      parentRoles?: string[],
    ) => {
      return children.map((child) => processNavigationItem(child, parentRoles));
    };

    return rawNavigation.map((item) => processNavigationItem(item));
  }, [roles]);

  return navigation || [];
};

export default useNavigation;
